<script setup lang="ts">
import { useCrud } from "~/composables/use-crud";
import type { User } from "@artech-global/types";

defineOptions({ name: "MemberFormModal" });

const props = defineProps<{
  workspaceID: string;
}>();

const emit = defineEmits<{ (e: "saved"): void }>();

const { t } = useI18n();
const toast = useToast();

type MemberFormModel = User & {
  role?: string;
  password?: string;
  phone?: string;
};

const defaultFormModel = (): MemberFormModel => ({
  id: "",
  email: "",
  name: "",
  phone: "",
  description: "",
  role: "",
  password: "",
});

const {
  formModel,
  formErrors,
  errorMsg,
  isCreate,
  saveLoading,
  loadLoading,
  reset: resetCrud,
  load,
  save,
} = useCrud<MemberFormModel>({
  endpoint: "/users",
  defaultFormModel,
  serialize: (model) =>
    model.id
      ? {
          id: model.id,
          email: model.email,
          workspaceID: props.workspaceID,
          name: model.name,
          phone: model.phone,
          role: model.role,
          description: model.description,
          password: model.password || undefined,
        }
      : {
          email: model.email,
          workspaceID: props.workspaceID,
          name: model.name,
          phone: model.phone,
          role: model.role,
          description: model.description,
        },
});

const isOpen = ref(false);

const roleOptions = computed(() => [
  { value: "owner", label: t("workspace.member.roleOwner") },
  { value: "admin", label: t("workspace.member.roleAdmin") },
  { value: "viewer", label: t("workspace.member.roleViewer") },
]);

const title = computed(() =>
  isCreate.value
    ? t("workspace.member.newTitle")
    : t("workspace.member.updateTitle"),
);

async function open(id?: string) {
  resetCrud();
  if (id) {
    try {
      await $fetchMember(id);
    } catch {
      if (errorMsg.value) toast.error({ title: errorMsg.value });
      return;
    }
  }
  isOpen.value = true;
}

const { $api } = useNuxtApp();

// useCrud.load() doesn't pass query params; the GetUser handler needs
// workspaceID to resolve the user's role for this workspace.
async function $fetchMember(id: string) {
  loadLoading.value = true;
  try {
    const data: any = await $api(`/users/${id}`, {
      query: { workspaceID: props.workspaceID },
    });
    Object.assign(formModel, {
      id: data.id || "",
      email: data.email || "",
      name: data.name || "",
      phone: data.phone || "",
      description: data.description || "",
      role: data.workspaceUser?.role || data.role || "",
      password: "",
    });
  } finally {
    loadLoading.value = false;
  }
}

async function submit() {
  const isCreating = isCreate.value;
  try {
    await save();
    isOpen.value = false;
    toast.success({
      title: t(isCreating ? "message.createSuccess" : "message.updateSuccess"),
    });
    emit("saved");
  } catch {
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  }
}

function close() {
  isOpen.value = false;
}

defineExpose({ open, close });
</script>

<template>
  <Modal
    v-model:open="isOpen"
    :title="title"
    size="md"
    @close="close"
  >
    <form
      class="flex flex-col gap-medium"
      autocomplete="off"
      @submit.prevent="submit"
    >
      <InlineMessage
        v-if="errorMsg && Object.keys(formErrors).length > 0"
        intent="negative"
      >
        {{ errorMsg }}
      </InlineMessage>

      <FormField
        v-model="formModel.name"
        :label="t('field.fullName')"
        :error="formErrors.name"
        required
      />

      <FormField
        v-model="formModel.email"
        type="email"
        name="member-email"
        autocomplete="off"
        :label="t('field.email')"
        :disabled="!isCreate"
        :error="formErrors.email"
        required
      />

      <FormField
        v-model="formModel.phone"
        type="tel"
        name="member-phone"
        autocomplete="off"
        :label="t('field.phone')"
        :error="formErrors.phone"
      />

      <FormSelect
        v-model="formModel.role"
        :options="roleOptions"
        :label="t('workspace.member.columnRole')"
        :placeholder="t('workspace.member.rolePlaceholder')"
        :error="formErrors.role"
      />

      <FormField
        v-if="!isCreate"
        v-model="formModel.password"
        type="password"
        name="member-password"
        autocomplete="new-password"
        :label="t('field.password')"
        :help-text="t('workspace.member.passwordHelp')"
        :error="formErrors.password"
      />

      <FormTextarea
        v-model="formModel.description"
        :label="t('field.description')"
        :error="formErrors.description"
      />
    </form>

    <template #actions>
      <Button variant="tertiary" size="medium" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="medium"
        :disabled="saveLoading"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
